import {
  parseCareOrderListResponse,
  postCareOrderList,
  readCareOrderListPageCount,
  type OrderCardDTO,
  type OrderListQuery,
} from '@/api/orders'

const DEFAULT_TTL_MS = 60_000

type CacheEntry = {
  expiresAt: number
  value: unknown
}

const resultCache = new Map<string, CacheEntry>()
const inFlight = new Map<string, Promise<unknown>>()

function normalizeQuery(params: OrderListQuery): OrderListQuery {
  return {
    page: params.page,
    pageSize: params.pageSize,
    searchField: params.searchField,
    searchQuery: params.searchQuery?.trim() || undefined,
    serviceDate: params.serviceDate?.trim() || undefined,
    followUpDate: params.followUpDate?.trim() || undefined,
    serviceStatus: params.serviceStatus?.trim() || undefined,
    technicianId: params.technicianId?.trim() || undefined,
    inspectorId: params.inspectorId?.trim() || undefined,
  }
}

export function careOrderListCacheKey(params: OrderListQuery): string {
  return JSON.stringify(normalizeQuery(params))
}

function readFreshEntry(key: string): unknown | null {
  const entry = resultCache.get(key)
  if (!entry) return null
  if (Date.now() > entry.expiresAt) {
    resultCache.delete(key)
    return null
  }
  return entry.value
}

function writeEntry(key: string, value: unknown, ttlMs: number): void {
  resultCache.set(key, { value, expiresAt: Date.now() + ttlMs })
}

/** 清空全部 list 缓存（测试或显式刷新时用） */
export function clearCareOrderListCache(): void {
  resultCache.clear()
  inFlight.clear()
}

/**
 * 按查询条件失效缓存；未传参时等同 {@link clearCareOrderListCache}。
 * 仅匹配 query 中显式给出的字段（undefined 表示忽略该字段）。
 */
export function invalidateCareOrderListCache(matcher?: Partial<OrderListQuery>): void {
  if (!matcher || !Object.keys(matcher).length) {
    clearCareOrderListCache()
    return
  }
  const fields = Object.keys(matcher) as (keyof OrderListQuery)[]
  const normalizedMatcher = normalizeQuery({ page: 1, pageSize: 1, searchField: 'order_id', ...matcher })
  for (const key of [...resultCache.keys()]) {
    try {
      const parsed = JSON.parse(key) as OrderListQuery
      const matches = fields.every((field) => {
        const expected = normalizedMatcher[field]
        if (expected === undefined) return true
        const actual = parsed[field]
        if (typeof expected === 'string') {
          return (actual ?? '').toString().trim() === expected.trim()
        }
        return actual === expected
      })
      if (matches) resultCache.delete(key)
    } catch {
      resultCache.delete(key)
    }
  }
}

export interface FetchCareOrderListCachedOptions {
  ttlMs?: number
  bypassCache?: boolean
}

/**
 * 带内存缓存与同参 in-flight 去重的 care-order/list 请求。
 * 默认 TTL 60s，覆盖初始化阶段重复拉数；assign/unassign 后应调用 invalidate。
 */
export async function fetchCareOrderListCached(
  params: OrderListQuery,
  opts?: FetchCareOrderListCachedOptions,
): Promise<unknown> {
  const query = normalizeQuery(params)
  const key = careOrderListCacheKey(query)
  const ttlMs = opts?.ttlMs ?? DEFAULT_TTL_MS

  if (!opts?.bypassCache) {
    const cached = readFreshEntry(key)
    if (cached !== null) return cached
  }

  const pending = inFlight.get(key)
  if (pending) return pending

  const requestPromise = postCareOrderList(query)
    .then((raw) => {
      writeEntry(key, raw, ttlMs)
      return raw
    })
    .finally(() => {
      inFlight.delete(key)
    })

  inFlight.set(key, requestPromise)
  return requestPromise
}

const ASSIGNED_LIST_PAGE_SIZE = 100

/**
 * 分页拉取 care-order/list 全量结果（用于 assigned 占用池，避免 pageSize 截断）。
 */
export async function fetchCareOrderListAllPages(
  base: Omit<OrderListQuery, 'page'>,
  opts?: FetchCareOrderListCachedOptions,
): Promise<{ list: OrderCardDTO[]; total: number }> {
  const pageSize = base.pageSize ?? ASSIGNED_LIST_PAGE_SIZE
  const firstRaw = await fetchCareOrderListCached({ ...base, page: 1, pageSize }, opts)
  const first = parseCareOrderListResponse(firstRaw)
  if (!first) return { list: [], total: 0 }

  const merged = [...first.list]
  const r = firstRaw as Record<string, unknown>
  const data = r?.data != null && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : null
  const pageCount = data ? readCareOrderListPageCount(data) : 0
  const lastPage =
    pageCount > 1
      ? pageCount
      : first.list.length < pageSize
        ? 1
        : Math.max(1, Math.ceil(first.total / pageSize))

  for (let page = 2; page <= lastPage; page++) {
    const raw = await fetchCareOrderListCached({ ...base, page, pageSize }, opts)
    const parsed = parseCareOrderListResponse(raw)
    if (!parsed?.list.length) break
    merged.push(...parsed.list)
    if (parsed.list.length < pageSize) break
  }

  return { list: merged, total: first.total }
}
